import { useCallback, useEffect, useRef } from "react";

import { PlayerState } from "../../store/store";
import { usePlayerStore } from "../../store/usePlayerStore";

interface SubscribeStoreOnce {
  subscribe: <T extends keyof PlayerState>(
    storeStateKey: T,
    cb: (value: PlayerState[T]) => void
  ) => void;
  unsubscribe: () => void;
}

/**
 * Subscribe to store value once. If the subscribe function is called again, previous subscribe has been cancelled.
 */
export const useSubscribeStoreOnce = (): SubscribeStoreOnce => {
  const { subscribe } = usePlayerStore();
  const subscribeRef = useRef<() => void>(undefined);

  const unsubscribe = () => {
    subscribeRef.current?.();
    subscribeRef.current = undefined;
  };

  const subscribeOnce = useCallback(
    <T extends keyof PlayerState>(
      storeStateKey: T,
      cb: (value: PlayerState[T]) => void
    ) => {
      unsubscribe();

      subscribeRef.current = subscribe(state => state[storeStateKey], cb);
    },
    [subscribe]
  );

  useEffect(() => {
    return () => {
      unsubscribe();
    };
  }, []);

  return {
    subscribe: subscribeOnce,
    unsubscribe
  };
};
